'use client';

import Box from '@enlighten/design-system/dist/Box';
import Button from '@enlighten/design-system/dist/Button';
import Grid from '@enlighten/design-system/dist/Grid';
import Stack from '@enlighten/design-system/dist/Stack';
import type { Theme } from '@enlighten/design-system/dist/styles';
import Typography from '@enlighten/design-system/dist/Typography';
import useMediaQuery from '@enlighten/design-system/dist/useMediaQuery';
import AddRounded from '@mui/icons-material/AddRounded';
import RemoveRounded from '@mui/icons-material/RemoveRounded';
import type { StaticImageData } from 'next/image';
import Image from 'next/image';
import { useEffect, useState } from 'react';

import GongJuYeoung from '@/assets/images/pages/company/people/GongJuYeoung.png';
import KangDaeHo from '@/assets/images/pages/company/people/KangDaeHo.png';
import KimTaeHo from '@/assets/images/pages/company/people/KimTaeHo.png';
import KimWonTae from '@/assets/images/pages/company/people/KimWonTae.png';
import LeeDongKi from '@/assets/images/pages/company/people/LeeDongKi.png';
import LeeYoungHo from '@/assets/images/pages/company/people/LeeYoungHo.png';
import ParkDaeHyun from '@/assets/images/pages/company/people/ParkDaeHyun.png';
import AnimatedNumberTypography from '@/components/pages/common/AnimatedNumberTypography';

const totalInvestment = 445;
const totalEmployee = 101;

const list = [
	[
		{
			image: LeeYoungHo,
			name: '이영호',
			position: '대표',
			career: `서울대학교 전기공학/수학 (경제학 부전공)\n전, 미래에셋대우 인프라금융본부`,
		},
		{
			image: KimWonTae,
			name: '김원태',
			position: 'CFO',
			career: `성균관대학교 경영학/공인회계사\n전, 삼일회계법인\n전, 웅진그룹 기획조정실 근무, 웅진씽크빅 CFO`,
		},
    {
      image: KangDaeHo,
      name: '강대호',
      position: 'CTO',
      career: `서울대학교 전기컴퓨터공학 박사\n전, 삼성전자`,
    },
	],
	[
    {
      image: KimTaeHo,
      name: '김태호',
      position: '건설',
      career: `서울대학교 전기공학\n전, 두나무\n전, 삼성전자`,
    },
    {
      image: LeeDongKi,
      name: '이동기',
      position: '자산관리',
      career: `충남대학교 산업공학 석사\n국내외 태양광 O&M 전문 운영\n전, 에스에너지`,
    },
    {
      image: ParkDaeHyun,
      name: '박대현',
      position: '에너지',
      career: `건국대학교 에너지시스템공학 박사\n에너지신사업 / R&D 담당`,
    },
	
	],
];

export default function Section5() {
	const [more, setMore] = useState<boolean>(false);

	const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'));

	const flattenedList = list.flat();

	useEffect(() => {
		setMore(!isMobile);
	}, [isMobile]);

	return (
		<Grid
			container
			direction="column"
			alignItems="center"
			width="100%"
			padding={{ xs: '60px 24px 0', md: '100px 24px 162px' }}
			sx={{
				'.animated-numbers': {
					height: 80,
				},
			}}
		>
			<Typography display={{ xs: 'none', md: 'block' }} fontSize={{ xs: 24, md: 40 }} fontWeight={700} textAlign="center" lineHeight={1.35} color="common.white">
				{'엔라이튼은\n에너지, IT, 금융분야 최고의 전문가들과 함께\n빠르게 성장하고 있습니다.'}
			</Typography>
			<Typography display={{ xs: 'block', md: 'none' }} fontSize={{ xs: 24, md: 40 }} fontWeight={700} textAlign="center" lineHeight={1.35} color="common.white">
				{'엔라이튼은\n에너지, IT, 금융분야\n최고의 전문가들과 함께\n빠르게 성장하고 있습니다.'}
			</Typography>

			<Grid container direction={{ xs: 'column', md: 'row' }} gap={2} justifyContent="center" alignItems={{ xs: 'center', md: 'flex-start' }} width="100%" marginY={10}>
				<Grid container direction="column" flex={1} alignItems="center" width="100%" maxWidth={260}>
					<Typography fontSize={18} fontWeight={700} textAlign="center" color="grey.600">
						누적 투자
					</Typography>
					<Typography fontSize={12} marginTop="4px" textAlign="center" color="grey.400" width="100%">
						(*관계사 누적 기준)
					</Typography>
					<Box display={{ xs: 'none', md: 'block' }} fontSize={48} fontWeight={700} color="common.white" margin="40px 0 36px">
						<AnimatedNumberTypography className="num pc" value={totalInvestment} suffix="억원" />
					</Box>
					<Box display={{ xs: 'block', md: 'none' }} fontSize={34} fontWeight={700} color="common.white" margin="20px 0">
						<AnimatedNumberTypography className="num mobile" value={totalInvestment} suffix="억원" />
					</Box>
				</Grid>
				<Grid container direction="column" flex={1} alignItems="center" width="100%" maxWidth={260}>
					<Typography fontSize={18} fontWeight={700} textAlign="center" color="grey.600">
						엔라이튼 팀
					</Typography>
					<Typography fontSize={12} marginTop="4px" textAlign="center" color="grey.400" width="100%">
						(*관계사 누적 기준)
					</Typography>
					<Box display={{ xs: 'none', md: 'block' }} fontSize={48} fontWeight={700} color="common.white" margin="40px 0 36px">
						<AnimatedNumberTypography className="num pc" value={totalEmployee} suffix="명" />
					</Box>
					<Box display={{ xs: 'block', md: 'none' }} fontSize={34} fontWeight={700} color="common.white" margin="20px 0">
						<AnimatedNumberTypography className="num mobile" value={totalEmployee} suffix="명" />
					</Box>
				</Grid>
			</Grid>
			{/* 데스크탑 */}
			{/* <Stack display={{ xs: 'none', md: 'flex' }} gap="56px 40px"> */}
			<Stack display={{ xs: 'none', md: 'flex' }} alignItems="center" gap={7}>
				{list.map((row, i) => (
					<Stack key={i} direction="row" gap={5}>
						{row.map(({ image, name, position, career }, i) => (
							<MemberCard key={i} image={image} name={name} position={position} career={career} />
						))}
					</Stack>
				))}
			</Stack>
			{/* 모바일 */}
			<Stack display={{ xs: 'flex', md: 'none' }} gridTemplateColumns="repeat(3, 1fr)" gap="30px" marginBottom={3}>
				{flattenedList.slice(0, more ? flattenedList.length : 3).map(({ image, name, position, career }, i) => (
					<MemberCard key={i} image={image} name={name} position={position} career={career} />
				))}
			</Stack>
			<Button
				onClick={() => {
					setMore((state) => !state);
				}}
				sx={{
					display: { xs: 'flex', md: 'none' },
					height: { xs: 44, md: 52 },
					padding: { xs: '9px 15px', md: '6px 23px' },
				}}
			>
				{more ? (
					<>
						<RemoveRounded fontSize="small" sx={{ marginRight: 1 }} />
						접기
					</>
				) : (
					<>
						<AddRounded fontSize="small" sx={{ marginRight: 1 }} />
						더보기
					</>
				)}
			</Button>
		</Grid>
	);
}

interface MemberCardProps {
	image: StaticImageData;
	name: string;
	position: string;
	career: string;
}

function MemberCard({ image, name, position, career }: MemberCardProps) {
	return (
		<Grid
			container
			direction="column"
			sx={{
				'& img': {
					maxWidth: { xs: '100%', md: 257 },
					height: 'auto',
				},
			}}
		>
			<Image src={image} alt={name} />
			<Typography fontSize={22} fontWeight={700} color="common.white" marginTop="30px">
				{name}
			</Typography>
			<Typography fontSize={16} fontWeight={700} lineHeight={1.5} color="#7F3FFF">
				{position}
			</Typography>
			<Typography fontSize={16} fontWeight={500} lineHeight={1.5} color="grey.600" marginTop="10px">
				{career}
			</Typography>
		</Grid>
	);
}
